import {Component, OnInit} from '@angular/core';
import {combineLatest, Observable, of} from 'rxjs';
import {Invoice} from '../../model/invoice';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {InvoicePeerService} from '../../service/invoice-peer.service';
import {PaymentHistoriesResource} from '../../model/model-resource';
import {switchMap, tap, map} from 'rxjs/operators';
import {ObjectQueryFilterOperator, ObjectQueryParameters, ObjectQuerySortDirection} from '../../model/object-query-parameters';
import {PaymentHistory} from '../../model/payment-history';
import {NotZero} from '../../validators/not-zero.validator';
import {UsersMapCache} from '../../model/model-map-cache';

@Component({
  selector: 'app-invoice-payment-history-widget',
  templateUrl: './invoice-payment-history-widget.component.html',
  styleUrls: ['./invoice-payment-history-widget.component.scss']
})
export class InvoicePaymentHistoryWidgetComponent implements OnInit {

  data$;

  invoice: Invoice;
  selectedItem: PaymentHistory;
  totalPaid: number = null;
  totalLeft: number = null;
  typeChoices = [
    {value: true, label: 'Payment'},
    {value: false, label: 'Compensation'}
  ];

  form: UntypedFormGroup;
  locked = false;
  added = false;
  saving = false;

  constructor(
    private invoicePeerService: InvoicePeerService,
    private paymentHistoriesResource: PaymentHistoriesResource,
    private usersMapCache: UsersMapCache,
    private builder: UntypedFormBuilder) {
    this.form = this.builder.group({
      sum: [null, [Validators.required, NotZero]],
      changed: [null, Validators.required],
      payment: [null, Validators.required],
      description: [null]
    });
  }

  add(): void {
    this.invoicePeerService.lock();
    this.added = true;
    this.form.reset();
    this.form.patchValue({
      sum: this.totalLeft,
      changed: new Date(),
      payment: true
    });
  }

  revert(): void {
    this.invoicePeerService.unlock();
    this.added = false;
  }

  save(): void {
    this.saving = true;
    const paymentHistory = new PaymentHistory();
    Object.assign(paymentHistory, this.form.value);
    paymentHistory.invoiceId = this.invoice.invoiceId;
    this.paymentHistoriesResource.save(paymentHistory).subscribe(() => {
      this.invoicePeerService.unlock();
      this.invoicePeerService.refresh();
      this.selectedItem = null;
      this.saving = false;
      this.added = false;
    });
  }

  itemClicked(item) {
    this.selectedItem = (item !== this.selectedItem ? item : null);
  }

  ngOnInit() {

    this.data$ = this.invoicePeerService
      .get()
      .pipe(
        tap(invoice => this.invoice = invoice),
        switchMap((invoice) => combineLatest([
          of(invoice),
          this.usersMapCache.map$,
          this.getPaymentHistories(invoice.invoiceId)
        ])),
        map(([invoice, users, paymentHistories]) => ({invoice, users, paymentHistories}))
      );

    this.invoicePeerService.getState().subscribe((state) => {
      if (state === 'locked') {
        this.locked = true;
      } else if (state === 'unlocked') {
        this.locked = false;
      }
    });
  }

  private getPaymentHistories(invoiceId: number): Observable<PaymentHistory[]> {
    const params = new ObjectQueryParameters();
    params.addFilter('invoiceId', ObjectQueryFilterOperator.Equals, invoiceId.toString());
    params.addSort('created', ObjectQuerySortDirection.Descending);
    return this.paymentHistoriesResource.query(params);
  }
}
